import React, { useCallback, useMemo, useRef } from 'react';
import { DashboardT } from 'business/definitions';
import {
    Recurrence,
    ScheduledDashboardEmail,
    ScheduledDashboardEmailRecipient,
} from 'components/DashboardSendModal/types';
import {
    Button,
    FormControl,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    useTheme,
} from '@mui/material';
import { useGetPossibleRecipients } from 'lib/alert/hooks/useFetchRecipients';
import { useTranslation } from 'react-i18next';
import DatePicker from 'components/inputs/DatePicker';
import { getRecurrenceOptions } from '../helpers';
import {
    usePendingScheduledEmailChanges,
    useScheduledEmailValidation,
} from '../hooks/scheduledDashboardsForm';
import EmailSenderSelector from './ScheduledDashboardEmailFormComponents/EmailSenderSelector';
import EmailFilterOptions from './ScheduledDashboardEmailFormComponents/EmailFilterOptions';
import EmailRecipientsSelector from './ScheduledDashboardEmailFormComponents/EmailRecipientsSelector';
import EmailPreviews from './ScheduledDashboardEmailFormComponents/EmailPreviews';
import Flex from 'ui/Flex/Flex';

type Props = {
    onSubmit: () => void;
    onGoBack: () => void;
    scheduledEmail: ScheduledDashboardEmail;
    setScheduledEmail: (arg1: ScheduledDashboardEmail) => void;
    dashboard: DashboardT;
};

/*
 * Form handling the config of scheduled dashboard emails.
 */
const ScheduledDashboardEmailForm = ({
    onSubmit,
    onGoBack,
    scheduledEmail,
    setScheduledEmail,
    dashboard,
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isEditing = scheduledEmail.id !== undefined;
    const today = useMemo(() => new Date(), []);
    const { data: users = [], isLoading } = useGetPossibleRecipients(
        dashboard.key,
    );
    const possibleRecipients = users.map(
        (user) =>
            ({
                recipientId: user.id,
                internalEmail: user.email,
            }) as ScheduledDashboardEmailRecipient,
    );

    const initialFirstSentDate = useRef(scheduledEmail.firstSentDate);
    const { errors, isValid, isValidDate } = useScheduledEmailValidation(
        scheduledEmail,
        initialFirstSentDate.current,
        possibleRecipients,
    );

    const recurrences = getRecurrenceOptions(isEditing, t);

    const hasPendingChanges = usePendingScheduledEmailChanges(
        scheduledEmail,
        isEditing,
    );

    const updateEmail = useCallback(
        (updates: Partial<ScheduledDashboardEmail>) =>
            setScheduledEmail({ ...scheduledEmail, ...updates }),
        [scheduledEmail, setScheduledEmail],
    );

    return (
        <FormControl sx={{ gap: theme.spacing(3), margin: theme.spacing(2) }}>
            <EmailSenderSelector
                allUsers={users}
                disabled={isLoading}
                scheduledEmail={scheduledEmail}
                updateEmail={updateEmail}
            />
            <TextField
                id="standard-multiline-static"
                variant="standard"
                label={t('sendDashboard.formTextLabel')}
                value={scheduledEmail.text}
                placeholder={t('sendDashboard.initialText')}
                onChange={(event) => updateEmail({ text: event.target.value })}
                multiline
                rows={7}
            />
            <DatePicker
                value={scheduledEmail.firstSentDate}
                label={t('sendDashboard.sendDate')}
                onChange={(inputDate) =>
                    updateEmail({ firstSentDate: inputDate })
                }
                min={today.toISOString().split('T')[0]}
                isValidDateProp={isValidDate}
                error={!!errors.firstSentDate}
                helperText={errors.firstSentDate && t(errors.firstSentDate)}
            />
            <TextField
                variant="standard"
                id="recurrence-select"
                label={t('sendDashboard.recurrence')}
                value={scheduledEmail.recurrence}
                SelectProps={{
                    // @ts-expect-error MUI type limitation : https://github.com/mui/material-ui/issues/34154
                    SelectDisplayProps: { 'data-testid': 'recurrence' },
                }}
                select
                onChange={(event) =>
                    updateEmail({
                        recurrence: event.target.value as Recurrence,
                    })
                }
            >
                {recurrences.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>
            <EmailFilterOptions
                scheduledEmail={scheduledEmail}
                updateEmail={updateEmail}
                dashboard={dashboard}
            />
            <EmailRecipientsSelector
                scheduledEmail={scheduledEmail}
                updateEmail={updateEmail}
                possibleRecipients={possibleRecipients}
                error={errors.recipients}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={scheduledEmail.shouldSendCopyToSender}
                        onChange={(event) =>
                            updateEmail({
                                shouldSendCopyToSender: event.target.checked,
                            })
                        }
                    />
                }
                label={t('sendDashboard.shouldSendCopyToSender')}
            />
            <EmailPreviews
                dashboard={dashboard}
                filterOption={scheduledEmail.filterOption}
            />
            <Flex gap={theme.spacing(2)} justifyContent="right">
                <Button variant="outlined" onClick={onGoBack}>
                    {t('common.return')}
                </Button>
                <Button
                    disabled={!hasPendingChanges || !isValid}
                    variant="contained"
                    onClick={onSubmit}
                >
                    {isEditing
                        ? t('sendDashboard.edit')
                        : t('sendDashboard.continue')}
                </Button>
            </Flex>
        </FormControl>
    );
};

export default ScheduledDashboardEmailForm;
